import { CitationTag } from "components/CitationTag";
import Popover from "components/Popover";
import { useConsensusMeter } from "features/ConsensusMeter";
import { Paper } from "helpers/api";
import { detailPageUrl } from "helpers/pageUrl";
import { SearchResultTextType } from "helpers/search";
import {
  MixpanelActionSource,
  MixpanelCiteType,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import { useAppSelector } from "hooks/useStore";
import { FeatureFlag, useFeatureGate } from "features/Statsig";
import Link from "next/link";
import React, { useCallback, useEffect, useState } from "react";
import { CitationTooltip } from "./CitationTooltip";
import classNames from "classnames";

export type CitationProps = {
  paperIndex: number; // Paper index within the analysis (0-based)
  paper?: Paper;
  quote?: string;
  quoteIndex?: number; // Quote index within paper (0-based)
  onClick?: (
    paper: Paper,
    paperIndex: number,
    referenceLineNumber: number,
    associatedSentence: string,
    quote?: string,
    quoteIndex?: number
  ) => void;
  searchId?: string;
  onCitePaper?: (
    paper: Paper,
    citeType: MixpanelCiteType,
    paperIndex: number,
    resultType?: SearchResultTextType
  ) => void;
  onFullTextClick?: (
    paper: Paper,
    linkType: MixpanelFullTextLinkType,
    actionSource: MixpanelActionSource,
    index?: number,
    libraryId?: string
  ) => void;
  sharePaperEventOverride?: (
    paper: Paper,
    paperIndex: number,
    shareType: string,
    resultType?: SearchResultTextType
  ) => void;
  onCitationFoundInAnalysis?: (
    paperIndex: number,
    quote?: string,
    quoteIndex?: number
  ) => void;
  hideCitation?: boolean;
  lastInGroup?: boolean;
  showCitationMeterResult?: boolean;
  hasUsedFullText?: boolean;
  referenceLineNumber?: number;
  associatedSentence?: string;
  scale?: number;
};

const Citation: React.FC<CitationProps> = ({
  paper,
  paperIndex,
  quote,
  quoteIndex,
  onClick,
  onCitePaper,
  onFullTextClick,
  sharePaperEventOverride,
  searchId,
  onCitationFoundInAnalysis,
  hideCitation,
  lastInGroup,
  showCitationMeterResult,
  hasUsedFullText,
  referenceLineNumber,
  associatedSentence,
  scale = 1,
}) => {
  const [popoverInstance, setPopoverInstance] = useState<any>(null);
  const { consensusMeterResults: yesNoData } = useConsensusMeter({
    searchParams: { searchId },
    disabled: true, // disabling so citations never fetch data directly but will still be updated when the cache is updated
  });
  const isMobile = useAppSelector((state) => state.setting.isMobile);
  const isPaperSidebarDrawerEnabled = useFeatureGate(
    FeatureFlag.PAPER_SIDEBAR_DRAWER
  ).value;

  useEffect(() => {
    // On render, call the callback to track the citation
    if (onCitationFoundInAnalysis) {
      if (quote && typeof quoteIndex === "number") {
        onCitationFoundInAnalysis(paperIndex, quote, quoteIndex);
      } else {
        onCitationFoundInAnalysis(paperIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHidePopover = useCallback(async () => {
    if (popoverInstance != null) {
      popoverInstance?.hide();
    }
  }, [popoverInstance]);

  const handleLinkClick = useCallback(
    (e?: React.MouseEvent) => {
      if (isMobile) {
        e?.preventDefault();
        popoverInstance?.show();
        return;
      }

      // If paper sidebar drawer is enabled, prevent default link behavior
      if (isPaperSidebarDrawerEnabled && paper) {
        e?.preventDefault();
        e?.stopPropagation();
      }

      // Pass quote info through onClick
      if (paper) {
        onClick?.(
          paper,
          paperIndex,
          referenceLineNumber ?? 0,
          associatedSentence ?? "",
          quote,
          quoteIndex
        );
      }
    },
    [
      isMobile,
      onClick,
      popoverInstance,
      isPaperSidebarDrawerEnabled,
      paper,
      paperIndex,
      quote,
      quoteIndex,
      referenceLineNumber,
      associatedSentence,
    ]
  );

  const handleShowInPaper = useCallback(() => {
    // Always trigger the onClick for "show-in-paper", even on mobile
    if (paper) {
      onClick?.(
        paper,
        paperIndex,
        referenceLineNumber ?? 0,
        associatedSentence ?? "",
        quote,
        quoteIndex
      );
    }
  }, [
    onClick,
    paper,
    paperIndex,
    quote,
    quoteIndex,
    referenceLineNumber,
    associatedSentence,
  ]);

  if (hideCitation) {
    return null;
  }

  if (!paper) {
    return (
      <CitationTag
        paperIndex={paperIndex}
        quoteIndex={quoteIndex}
        size="sm"
        className={!lastInGroup ? "mr-1" : undefined}
        showFullTextIcon={hasUsedFullText}
        style={{ transform: `scale(${scale})` }}
      />
    );
  }

  const meter =
    showCitationMeterResult && paper
      ? yesNoData?.result_id_to_answer?.[paper.paper_id]
      : undefined;

  const { url_slug, paper_id, display_text } = paper;
  let absoluteLink = detailPageUrl(url_slug, paper_id);
  let text;
  let textType;
  if (display_text) {
    text = display_text;
    textType = SearchResultTextType.KEY_TAKEAWAY;
  }

  const shouldUseExternalLink =
    !isMobile && absoluteLink && !isPaperSidebarDrawerEnabled;
  const linkHref = shouldUseExternalLink ? absoluteLink : "#";
  const linkTarget = shouldUseExternalLink ? "_blank" : undefined;

  return (
    <Popover
      interactive
      appendToParent
      onShown={(instance: any) => {
        setPopoverInstance(instance);
      }}
      onHidden={(instance: any) => {
        setPopoverInstance(instance);
      }}
      childrenClassName={lastInGroup ? "" : "mr-1"}
      className="z-20 pb-2"
      maxWidth="380px"
      yOffset={4 + ((20 * scale) / 2 - 20 / 2)} // 20px is the approx height of the citation tag
      tooltipContent={
        <CitationTooltip
          paper={paper}
          onClick={handleLinkClick}
          onShowInPaperClick={handleShowInPaper}
          text={text}
          textType={textType}
          paperIndex={paperIndex}
          onActionTriggered={handleHidePopover}
          absoluteLink={absoluteLink}
          meter={meter}
          mixpanelActionSource={MixpanelActionSource.ProSearch}
          onCitePaper={onCitePaper}
          onFullTextClick={onFullTextClick}
          sharePaperEventOverride={sharePaperEventOverride}
          hasUsedFullText={hasUsedFullText}
          quote={quote}
          quoteIndex={quoteIndex}
        />
      }
    >
      <Link
        href={linkHref}
        onClick={onClick ? handleLinkClick : undefined}
        target={linkTarget}
      >
        <CitationTag
          paperIndex={paperIndex}
          quoteIndex={quoteIndex}
          variant={meter}
          size="sm"
          showFullTextIcon={hasUsedFullText}
          style={{ transform: `scale(${scale})` }}
        />
      </Link>
    </Popover>
  );
};

export default Citation;

/**
 * Converts citation data to markdown format for plain text export
 * Simply render the [number] format so that the export analysis button can process it into the proper citation
 */
export const CitationMarkdown = (props: any) => {
  const { paperIndex, paper_id, papers } = props;

  if (paper_id && papers) {
    const foundIndex = papers.findIndex((p: any) => p.paper_id === paper_id);
    return foundIndex !== -1 ? `[${foundIndex + 1}]` : "";
  }

  if (paperIndex !== undefined) {
    return `[${paperIndex + 1}]`;
  }

  return "";
};
